import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Autocomplete,
  CircularProgress,
  FormControl,
  TextField
} from '@mui/material';
import { MAIN_THEME } from '@styles';

import FormFieldWrapper from './FormFieldWrapper';
import { IFormFieldInputBaseProps } from '.';

import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

export interface IAutocompleteFormFieldProps extends IFormFieldInputBaseProps {
  name: string;
  value: string;
  fetchOptions: (query: string) => Promise<Record<string, string>>;
  title?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  freeSolo?: boolean;
  initialOptions?: Record<string, string>;
  onChange?: (value: string) => void;
  onBlur?: (
    value: string,
    e?: React.FocusEvent<HTMLDivElement, Element>
  ) => void;
  classes?: { input?: string; wrapper?: string };
  onResolveValue?: (payload: { id: string; label: string }) => void;
  disableClearable?: boolean;
  excludeIds?: string[];
}

interface IOption {
  id: string;
  label: string;
}

const useStyles = makeStyles<{ error?: string }>()((theme, { error }) => ({
  inputContainerStyles: { width: '100%' },
  autocomplete: {},
  inputStyles: {
    width: '100%',
    border: `1px solid ${MAIN_THEME.color.white86}`,
    borderRadius: '5px',
    outline: 'none',
    '& .MuiInputBase-input': {
      fontFamily: MAIN_THEME.font.regular,
      '&::placeholder': {
        fontStyle: 'italic',
        fontFamily: MAIN_THEME.font.regular,
        color: MAIN_THEME.color.white86,
        opacity: 1,
        fontSize: MAIN_THEME.fontSize.sm
      }
    },
    '& .MuiInputBase-root': {
      fontSize: MAIN_THEME.fontSize.sm,
      lineHeight: '15px',
      minHeight: '35px',
      width: '100%',
      padding: '0 25px 0 15px!important',
      margin: '0 auto',
      backgroundColor: MAIN_THEME.color.white98,
      outlineColor: error ? theme.palette.error.main : undefined,
      border: error
        ? `1px solid ${theme.palette.error.main}`
        : `0px solid ${MAIN_THEME.color.white92}`,
      boxSizing: 'border-box',
      borderRadius: '5px',
      '&::before': { display: 'none' },
      '&::after': { display: 'none' }
    }
  },
  optionDesign: {
    fontSize: `${MAIN_THEME.fontSize.sm}`,
    lineHeight: '15px',
    color: MAIN_THEME.color.black,
    fontFamily: MAIN_THEME.font.regular
  },
  paginationIcons: {
    display: 'flex',
    gap: '5px',
    justifyContent: 'end',
    alignItems: 'center',
    fontSize: MAIN_THEME.fontSize.sm,
    padding: '5px 7px 0 0',
    '& .MuiSvgIcon-root': { width: '0.8em' },
    '& .MuiIconButton-root': { padding: '2px' }
  },
  loader: {
    position: 'absolute',
    top: 'calc(50% - 12px)',
    right: '6%',
    opacity: 1,
    height: '25px',
    width: '25px'
  },
  selectMaterial: {
    '& .MuiInput-input': {
      fontSize: MAIN_THEME.fontSize.sm,
      padding: '7px 4px 0 0 !important',
      color: `${MAIN_THEME.color.black}`,
      '&::placeholder': {
        fontStyle: 'italic',
        fontFamily: MAIN_THEME.font.regular,
        color: MAIN_THEME.color.white86,
        opacity: 1,
        fontSize: MAIN_THEME.fontSize.sm
      }
    },
    '& .MuiInputBase-root': { height: '34.5px' },
    '& .Mui-disabled:before': { borderBottom: '1px solid rgba(0,0,0,.42)' },
    '& .MuiInputBase-root:before': {
      border: 'none',
      backgroundColor: 'unset',
      borderRadius: '0',
      borderBottom: `1px solid ${MAIN_THEME.color.white92}`
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInputBase-root:after':
      { borderBottom: `1px solid ${MAIN_THEME.color.white92}` }
  },
  selectError: {
    '& .MuiInputBase-root:before': {
      borderBottom: `1px solid ${theme.palette.error.main}`
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInputBase-root:after':
      { borderBottom: `1px solid ${theme.palette.error.main}` },
    '& .MuiInputBase-root:focus': {
      borderColor: theme.palette.error.main,
      outlineColor: theme.palette.error.main,
      boxShadow: 'none',
      borderRadius: 0
    },
    '& .MuiInputBase-root': {
      borderColor: `${theme.palette.error.main}`,
      outlineColor: `${theme.palette.error.main}`
    }
  },
  clearBtnContainer: { marginRight: '10px' },
  clearBtn: { cursor: 'pointer', height: '12px', width: '12px' }
}));

const AutocompleteFormField: React.FC<IAutocompleteFormFieldProps> = ({
  name,
  value,
  placeholder,
  error,
  material,
  disabled,
  freeSolo = true,
  initialOptions,
  fetchOptions,
  onChange,
  onBlur,
  onResolveValue,
  inputOnly = false,
  classes = {},
  title,
  className,
  loader,
  style,
  disableClearable = false,
  excludeIds = []
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<IOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const isSearchingRef = useRef(false);
  const lastSearchKeywordRef = useRef('');
  const lastSearchOptionsRef = useRef<IOption[]>([]);
  const { classes: inputClasses } = useStyles({ error });

  const recordToArray = (rec: Record<string, string>) =>
    Object.entries(rec || {}).map(([id, label]) => ({ id, label }));

  const [internalExcludeIds, setInternalExcludeIds] =
    useState<string[]>(excludeIds);

  useEffect(() => {
    setInternalExcludeIds(excludeIds);
  }, [excludeIds.join(',')]);

  const memoExcludeIds = useMemo(
    () => internalExcludeIds.slice(),
    [internalExcludeIds.join(',')]
  );

  const resolveSelectedOption = useCallback(
    (id: string): IOption | null => {
      const fromOptions = options.find((o) => o.id === id);
      if (fromOptions) return fromOptions;

      const fromLastSearch = lastSearchOptionsRef.current.find((o) => o.id === id);
      if (fromLastSearch) return fromLastSearch;

      if (initialOptions?.[id]) {
        return { id, label: initialOptions[id] };
      }

      return null;
    },
    [options, initialOptions]
  );

  useEffect(() => {
    const filtered = Object.fromEntries(
      Object.entries(initialOptions || {}).filter(
        ([id]) => !memoExcludeIds.includes(id)
      )
    );
    const newOptions = recordToArray(filtered);

    setOptions((prev) => {
      if (
        open ||
        (lastSearchOptionsRef.current.length > newOptions.length &&
          lastSearchKeywordRef.current.length >= 3)
      ) {
        return prev.length > 0 ? prev : newOptions;
      }

      const prevIds = prev.map((o) => o.id).join(',');
      const newIds = newOptions.map((o) => o.id).join(',');
      if (prevIds !== newIds) return newOptions;

      return prev;
    });
  }, [initialOptions, memoExcludeIds, open]);

  const [selectedOption, setSelectedOption] = useState<IOption | null>(null);

  useEffect(() => {
    if (isSearchingRef.current) return;

    if (!value) {
      setSelectedOption(null);
      return;
    }

    const found = resolveSelectedOption(value);
    setSelectedOption(found);

    if (found && !open) {
      setInputValue(found.label);
    }
  }, [value, open, resolveSelectedOption]);

  const fetchLov = useCallback(
    async (searchKeyword: string) => {
      if (searchKeyword.length < 3) return;

      lastSearchKeywordRef.current = searchKeyword;
      setLoading(true);
      try {
        const res = await fetchOptions(searchKeyword);
        const filtered = Object.fromEntries(
          Object.entries(res).filter(([id]) => !excludeIds?.includes(id))
        );
        const nextOptions = recordToArray(filtered);
        lastSearchOptionsRef.current = nextOptions;
        setOptions(nextOptions);
      } finally {
        setLoading(false);
      }
    },
    [fetchOptions, excludeIds]
  );

  const clearSelection = () => {
    isSearchingRef.current = false;
    lastSearchKeywordRef.current = '';
    lastSearchOptionsRef.current = [];
    onChange?.('');
    setSelectedOption(null);
    setInputValue('');
    setOptions(recordToArray(initialOptions));
  };

  /** Handle text typing */
  const handleInputChange = (_: any, newInputValue: string, reason: string) => {
    if (reason === 'clear') {
      clearSelection();
      return;
    }

    if (reason === 'reset') {
      return;
    }

    setInputValue(newInputValue);

    if (reason === 'input') {
      isSearchingRef.current = true;
      const selectedLabel = selectedOption?.label ?? '';
      if (newInputValue.trim() !== selectedLabel.trim()) {
        setSelectedOption(null);
        if (value) {
          onChange?.('');
        }
      }
      void fetchLov(newInputValue);
    }
  };

  /** Handle selection */
  const handleChange = (_: any, selected: IOption | string | null) => {
    if (!selected) {
      clearSelection();
      return;
    }

    isSearchingRef.current = false;

    if (typeof selected === 'string') {
      onChange?.(selected);
      onResolveValue?.({ id: selected, label: selected });
      setInputValue(selected);
    } else {
      onChange?.(selected.id);
      onResolveValue?.(selected);
      setInputValue(selected.label);
      setSelectedOption(selected);
    }
  };

  const handleFocus = () => {
    setOpen(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement, Element>) => {
    isSearchingRef.current = false;

    if (value) {
      const found = resolveSelectedOption(value);
      if (found) {
        setSelectedOption(found);
        setInputValue(found.label);
      }
    }

    onBlur?.(value, e);
  };

  /** On open → restore the last search results */
  const handleOpen = () => {
    setOpen(true);

    const searchTerm =
      isSearchingRef.current && inputValue.length >= 3
        ? inputValue
        : lastSearchKeywordRef.current.length >= 3
          ? lastSearchKeywordRef.current
          : inputValue.length >= 3
            ? inputValue
            : '';

    if (searchTerm) {
      void fetchLov(searchTerm);
      return;
    }

    if (lastSearchOptionsRef.current.length > 0) {
      setOptions(lastSearchOptionsRef.current);
      return;
    }

    if (options.length === 0 && initialOptions) {
      setOptions(recordToArray(initialOptions));
    }
  };

  const renderInput = () => {
    return (
      <FormControl
        error={!!error}
        disabled={disabled}
        className={inputClasses.inputContainerStyles}
        size="small"
        variant="outlined"
        fullWidth
      >
        <Autocomplete
          className={inputClasses.autocomplete}
          forcePopupIcon
          open={open}
          onOpen={handleOpen}
          onClose={() => setOpen(false)}
          disabled={disabled}
          freeSolo={freeSolo}
          disableClearable={disableClearable}
          selectOnFocus={false}
          options={options}
          value={selectedOption} // hide value only while typing
          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
          isOptionEqualToValue={(opt, val) => {
            if (typeof opt === 'string' || typeof val === 'string')
              return opt === val;

            return opt.id === val.id;
          }}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          loading={loading}
          noOptionsText={loading ? 'Fetching data...' : 'No data'}
          renderOption={(props, option) => {
            const { key, ...rest } = props;

            return (
              <li key={key} {...rest}>
                <span className={inputClasses.optionDesign}>
                  {(option as any).label}
                </span>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              name={name}
              placeholder={placeholder}
              className={clsx(
                !material && inputClasses.inputStyles,
                material && inputClasses.selectMaterial,
                error && inputClasses.selectError
              )}
              error={!!error}
              InputProps={{
                ...params.InputProps,
                startAdornment: params.InputProps.startAdornment,
                endAdornment: <>{params.InputProps.endAdornment}</>
              }}
            />
          )}
        />
      </FormControl>
    );
  };

  return inputOnly ? (
    renderInput()
  ) : (
    <>
      <FormFieldWrapper
        title={title}
        error={error}
        name={name}
        className={clsx(classes.wrapper, className)}
        style={style}
        material={material}
      >
        {renderInput()}
        {loader && (
          <CircularProgress
            className={inputClasses.loader}
            size={20}
            thickness={3}
          />
        )}
      </FormFieldWrapper>
    </>
  );
};

export { AutocompleteFormField };
